import { type IntlShape, useIntl } from 'react-intl';

import type { AccountType } from '@/api/schema';
import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import { t, translate } from '@/lib/utils';
import { type UserProfile, useAccountStore } from '@/stores/accountStore';
import { useUserStore } from '@/stores/userStore';

interface InitialState {
    account: AccountType | null;
    userProfile: UserProfile | null;
    countryIntl?: IntlShape;
    initialStates?: {
        chargingCards: ChargingCardsSchemaType;
        invoiceData: InvoiceDataSchemaType;
        shippingAddress: ShippingSchemaType;
        support: {
            label: string;
            phone: string;
        };
    };
}

export const useInitialState = (): InitialState => {
    const intl = useIntl();
    const { account, countryIntl } = useUserStore();

    const { userProfile } = useAccountStore();

    if (!account || !userProfile) {
        return { account: null, userProfile: null };
    }

    const { address, name, vatNumber } = account;
    const email = userProfile.email;

    return {
        account,
        userProfile,
        countryIntl: intl,
        initialStates: {
            chargingCards: {
                cardsNumber: 1,
                cardsListCustomization: [{ line1: '', line2: '' }],
            },
            invoiceData: {
                company: {
                    customerName: name,
                    companyRegistrationNumber: '',
                    address: address.firstAddressLine || '',
                    address2: address.secondAddressLine || '',
                    postCode: address.zip || '',
                    city: address.city,
                    country: address.countryCode,
                },
                contact: {
                    personalEmailAddress: email ?? '',
                    languageOfCommunication: '',
                    countryCode: address.countryCode,
                    phoneNumber: '',
                },
                invoicing: {
                    invoiceEmailAddress: '',
                    country: address.countryCode ?? '',
                    vat: vatNumber,
                    schemeId: undefined,
                    participantIdentifier: '',
                },
            },
            shippingAddress: {
                address: address.firstAddressLine || '',
                address2: address.secondAddressLine || '',
                postCode: address.zip || '',
                city: address.city || '',
                country: address.countryCode ?? '',
                addressee: '',
                addressType: 'company',
            },
            support: {
                label: countryIntl ? translate('cardDetails.supportLabel', countryIntl) : t('cardDetails.supportLabel'),
                phone: countryIntl ? translate('cardDetails.supportPhone', countryIntl) : t('cardDetails.supportPhone'),
            },
        },
    };
};
